import { Builder } from '@builder.io/react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useNavigate } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { generateLinkWithLang } from '../../../../../utils/linkGenerator';
import { useTranslation } from 'react-i18next';
import { getPageType } from '../../../../../routes';
import 'react-lazy-load-image-component/src/effects/blur.css';
import IsGTMScriptLoaded from '../../../../../utils/isGTMScriptLoaded';
import { selectSelectedCurrency } from '../../../../../store/user';
import { selectProducts } from '../../../../../store/products';
import CustomButton from '../../../../general/button';
import services from '../../../../../services';
import { getCreatorCodesWithDates } from '../../../../../pages/MATWAppeal/dates';
import { calculateCampaigns } from '../../../../../pages/MATWAppeal/campaignCalculation';
import { useKeenSlider } from 'keen-slider/react';
import { LiaAngleLeftSolid, LiaAngleRightSolid } from 'react-icons/lia';
import Arrow from '../../../../general/heroTab/tab-arrow';

const FundRaiserCardList = ({ className, usedOnTab = false, isHomePage = false, products = '' }) => {
  const selectedCurrencyStore = useSelector(selectSelectedCurrency);
  const [selectedCurrency, setSelectedCurrency] = useState(selectedCurrencyStore || 'AUD');
  const [isLoaded, setIsLoaded] = useState(false);
  const [appealProducts, setAppealProducts] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const allProducts = useSelector(state => selectProducts(state));

  const MutationPlugin = slider => {
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        slider.update();
      });
    });
    const config = { childList: true };

    slider.on('created', () => {
      observer.observe(slider.container, config);
    });
    slider.on('destroyed', () => {
      observer.disconnect();
    });
  };

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      mode: 'snap',
      loop: true,
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
      slides: {
        origin: 'auto',
        perView: 1,
        spacing: 8,
      },
    },
    [MutationPlugin],
  );

  const handleViewItemDataLayer = (tempProduct, currency) => {
    const dataLayerTemp = {
      event: 'view_item', //GA
      ecommerce: {
        currency: currency.toUpperCase(),
        items: [
          {
            item_id: tempProduct.creator,
            item_name: tempProduct.name,
            item_brand: tempProduct.category || '',
            item_category: tempProduct.category || '',
            item_description: tempProduct.description || '',
            price: tempProduct[currency.toLowerCase()],
          },
        ],
      },
    };
    window.dataLayer = window.dataLayer || [];
    if (IsGTMScriptLoaded()) {
      window.dataLayer.push(dataLayerTemp);
    }

    // Add Adobe Analytics event
    if (!window.adobeDataLayer) {
      window.adobeDataLayer = [];
    }
    window.adobeDataLayer.push({
      event: 'impression_item',
      page_info: {
        page_url: window.location.href,
        page_title: document.title,
        page_type: getPageType(window.location.pathname),
        language: i18n.language
      },
      items: [{
        item_id: tempProduct.creator,
        item_name: tempProduct.name,
        item_brand: tempProduct.category || "",
        item_category: tempProduct.category || "",
        item_description: tempProduct.description || '',
        price: tempProduct[currency.toLowerCase()],
        quantity: 1,
        currency: currency.toUpperCase()
      }]
    });
  };

  const ShowCurrencies = (currency, _price) => {
    const tempCurrency = currency?.toUpperCase();
    let price = Number(_price);
    const options = {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };
    if (price && Number(price) % 1 !== 0) {
      price = Number(price).toLocaleString('en', options);
    }
    let final = '';
    price = price ? price.toLocaleString() : '0';
    switch (tempCurrency) {
      case 'GBP':
      case 'EUR':
        final = `${getSymbolFromCurrency(tempCurrency.toLowerCase())}${price}`;
        break;
      case 'MYR':
        final = `${price}`;
        break;
      case 'IDR':
        final = `Rp${price}`;
        break;
      case 'AED':
        final = `Dh${price}`;
        break;
      default:
        final = `$${price}`;
        break;
    }
    return final;
  };

  const handlePercentage = (total_sale, goal) => {
    return `${((Number(total_sale) / Number(goal)) * 100).toFixed(2)}%`
  };

  const handleDaysLeft = (campaign) => {
    // Default to 30 days for now, you can implement actual campaign date logic
    return 30;
  };

  const getLatestOrdersVersion3 = async () => {
    // Parse products string to array of creators
    const creatorsArray = products ? products.split(',').map(creator => creator.trim()).filter(creator => creator.length > 0) : [];
    
    // Only fetch if creators are specified
    if (creatorsArray.length === 0) {
      setAllOrders({ data: [] });
      return;
    }
    
    // Create custom creator objects for the specified creators
    const creatorsToFetch = creatorsArray.map(creator => ({
      id: creator,
      start_date: '2025-01-01', // Default dates
      end_date: '2025-12-31',
      campaign: 'custom'
    }));
    
    let req = {
      products: creatorsToFetch,
      currency: selectedCurrency
    };
    try {
      const response = await services.getLatestOrdersV3(req);
      setAllOrders(response.data);
    } catch (error) {
      console.error('error========', error);
    }
  };

  useEffect(() => {
    if (allOrders && allProducts && Object.keys(allProducts).length) {
      // Parse products string to array of creators
      const creatorsArray = products ? products.split(',').map(creator => creator.trim()).filter(creator => creator.length > 0) : [];
      
      // If no creators specified, show empty array
      if (creatorsArray.length === 0) {
        setAppealProducts([]);
        setIsLoaded(true);
        return;
      }
      
      const { appealProducts } = calculateCampaigns({
        allProducts,
        _prds: allOrders,
        selectedCurrency,
        campaign: 'palestine'
      });
      
      // Filter appealProducts based on the products prop
      const filteredAppealProducts = appealProducts.filter(product => 
        creatorsArray.includes(product.creator)
      );
      
      setAppealProducts(filteredAppealProducts);
      setIsLoaded(true);
    }
  }, [allOrders, allProducts, selectedCurrency, products]);

  useEffect(() => {
    selectedCurrency && getLatestOrdersVersion3();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency]);

  useEffect(() => {
    setSelectedCurrency(selectedCurrencyStore || 'AUD');
  }, [selectedCurrencyStore]);

  const renderProductCard = (product, index) => (
    <div key={`product-${product.creator || product.id}-${index}`} className="w-full shadow-lg bg-white overflow-hidden mx-auto">
      <div className="overflow-hidden w-full">
        <div
          className="overflow-hidden cursor-pointer max-h-[395px] w-full flex items-center"
          onClick={() => navigate(generateLinkWithLang(`/appeals/${product.link || product.creator}`, lang))}
        >
          {product ? product.image_link
            ? <LazyLoadImage
                alt={product.alt ? product.alt : product.name}
                effect="blur"
                width={`100%`}
                delayTime={500}
                afterLoad={() => {
                  handleViewItemDataLayer(product, selectedCurrency)
                }}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = "/images/general/logo-replace.png";
                }}
                src={product.image_link}
              />
            : <img className='w-full' src="/images/general/logo-replace.png" alt="" />
            : <Skeleton height={344} />
          }
        </div>
      </div>
      <div className="p-2 md:p-3 md:pb-5">
        <h3
          className="text-[#00a3da] sm:text-md md:text-md lg:text-lg xl:text-xl font-medium h-[48px] lg:h-[56px] mb-2 flex items-center cursor-pointer"
          onClick={() => navigate(generateLinkWithLang(`/appeals/${product.link || product.creator}`, lang))}
        >
          {product ? product.alternativeName || product.name : <Skeleton height={56} />}
        </h3>
        <div className="relative">
          {product ? <>
            <div className={`my-2 relative overflow-hidden max-h-[60px] md:max-h-[80px] ${(product.short_description || product.description).split(' ').length > 28 ? 'mb-4-' : 'mb-2-'}`}>
              <p className={`text-[#78716C] text-xs sm:text-sm overflow-hidden transition-all duration-200 min-h-[79px]`}>
                {product.short_description || product.description}
              </p>
            </div>
          </> : <Skeleton count={4} />}
        </div>
        <div className="basis-full flex-col md:basis-1/2 flex justify-center pt-1 md:pt-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex w-full text-sm">
              <strong className='text-[#00a3da] font-bold mr-0.5'>{product.totalSupporters || product.total_count || 0}</strong>
              <span className='inline-block lg:w-auto mr-1'>Supporters,</span>
              <strong className='text-[#00a3da] font-bold mr-0.5'>{handleDaysLeft(product.campaign)}</strong>
              <span className='inline-block lg:w-auto'>days left</span>
            </div>
          </div>
          <div className="flex w-full h-2 bg-[#E3E3E3] bg-opacity-10- mb-2">
            <div
              className={`bg-[#F60362]`}
              style={{ width: `${handlePercentage(product.totalRaised || product.total_sale || 0, product.goal || 500000)}` }}></div>
          </div>
        </div>
        <div className="basis-full flex justify-between items-center mt-2">
          <div className="flex flex-col w-full">
            <div className='text-lg md:text-2xl text-[#00a3da] basis-full font-bold flex items-center'>
              {product.totalRaised && ShowCurrencies(selectedCurrency, Math.round(product.totalRaised), false)}
              <span className='text-xs ml-0.5'>{selectedCurrency}</span>
            </div>
            <div className="flex flex-wrap w-full text-xs items-center">
              <span className='text-[#78716C] text-xs'>Raised of</span>
              <div className='text-[#00a3da] font-bold mx-1 text-sm'>{ShowCurrencies(selectedCurrency, Math.round(product.goal || 500000))}</div>
              Goal
            </div>
          </div>
          {product
            ? <CustomButton
                onClick={() => navigate(generateLinkWithLang(`/appeals/${product.link || product.creator}`, lang))} type="button"
                title={'Donate'}
                className={`!rounded-none`} />
            : <Skeleton height={43} />
          }
        </div>
      </div>
    </div>
  );

  return (
    <section
      className={`${className ? className : 'bg-LighterBlue'} w-full overflow-hidden px-4 md:px-0 ${usedOnTab ? 'py-1 md:py-2' : 'py-4 md:py-12'}`}
    >
      <div className="md:container w-full mx-auto">  
        <div className="hidden flex-wrap gap-3 md:grid md:grid-cols-3 md:gap-4 justify-center items-start w-full">
          {isLoaded ? (
            appealProducts.length > 0 ? (
              appealProducts.map((product, index) => (
                <div key={`desktop-${product.creator || product.id}-${index}`} className={`${isHomePage ? 'basis-1/2 lg:basis-1/3 xl:basis-1/4' : 'basis-1/3'} flex justify-center px-2 py-4`}>
                  {renderProductCard(product, index)}
                </div>
              ))
            ) : (
              <p className="text-center w-full">No appeal products available.</p>
            )
          ) : (
            <Skeleton count={4} />
          )}
        </div>

        {/* Mobile Layout with Carousel */}
        <div className="md:hidden w-full flex-wrap justify-center items-center relative">
          {appealProducts.length > 1 && (
            <div className="hidden- md:flex ">
              {instanceRef.current && (
                <div
                  className={`absolute flex justify-center items-center translate-y-1/2 w-8 h-8 top-[47%] -left-[16px] z-10 cursor-pointer text-white`}
                  onClick={e => e.stopPropagation() || instanceRef.current?.prev()}
                >
                  <LiaAngleLeftSolid size={25} />
                </div>
              )}
              {instanceRef.current && (
                <div
                  className={`absolute flex justify-center items-center translate-y-1/2 w-8 h-8 top-[47%] -right-[16px] z-10 cursor-pointer text-white`}
                  onClick={e => e.stopPropagation() || instanceRef.current?.next()}
                >
                  <LiaAngleRightSolid size={25} />
                </div>
              )}
            </div>
          )}
          
          {isLoaded ? (
            appealProducts.length > 0 ? (
              appealProducts.length > 1 ? (
                <div ref={sliderRef} className={`keen-slider !w-[94%] min-[1280px]:!w-[94%] mx-auto`}>
                  {appealProducts.map((product, index) => (
                    <div key={`mobile-${product.creator || product.id}-${index}`} className="keen-slider__slide basis-full flex justify-center py-4">
                      <div className="w-full max-w-[300px]-">
                        {renderProductCard(product, index)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="!w-[94%] min-[1280px]:!w-[94%] mx-auto">
                  {appealProducts.map((product, index) => (
                    <div key={`mobile-single-${product.creator || product.id}-${index}`} className="basis-full flex justify-center py-4">
                      <div className="w-full max-w-[300px]-">
                        {renderProductCard(product, index)}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <p className="text-center w-full">No appeal products available.</p>
            )
          ) : (
            <Skeleton count={4} />
          )}

          {/* Mobile Navigation Dots */}
          {appealProducts.length > 1 && (
            <div className="flex bg-white py-3 px-2 rounded-md justify-center items-center mx-auto mt-2 max-w-[300px]">
              {loaded && instanceRef.current && (
                <div className="flex justify-center items-center relative w-full">
                  <Arrow left onClick={e => e.stopPropagation() || instanceRef.current?.prev()} disabled={currentSlide === 0} />
                  {[...Array(instanceRef.current.track.details.slides.length).keys()].map(idx => {
                    return (
                      <button
                        key={`q${idx}`}
                        onClick={() => {
                          instanceRef.current?.moveToIdx(idx);
                        }}
                        className={
                          'md:w-4 md:mx-2 mx-1 md:h-4 h-3  w-3 rounded-full ' +
                          (currentSlide === idx ? ' bg-[#F60362] hover:opacity-60' : 'bg-[#C3C3C3] hover:bg-[#F60362')
                        }
                      ></button>
                    );
                  })}
                  <Arrow
                    onClick={e => e.stopPropagation() || instanceRef.current?.next()}
                    disabled={currentSlide === instanceRef.current.track.details.slides.length - 1}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

Builder.registerComponent(FundRaiserCardList, {
  name: 'FundRaiserCardList',
  inputs: [
    {
      name: 'className',
      type: 'string',
    },
    {
      name: 'usedOnTab',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'isHomePage',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'products',
      type: 'string',
      defaultValue: '',
      helperText: 'Enter creator codes separated by commas (e.g., EMR-PAL-PO-25-00, EMR-PAL-PO-25-01). Leave empty to show no products.',
    },
  ],
});

export default FundRaiserCardList; 